import apiClient from './api';
import { ApiResponse } from '../types';

export interface Channel {
  id: string;
  organization_id: string;
  name: string;
  channel_type: string;
  status: string;
  credentials: {
    bot_username?: string;
    bot_id?: number;
    webhook_url?: string;
    webhook_secret?: string;
    page_id?: string;
    page_name?: string;
    instagram_account_id?: string;
  };
  settings: Record<string, unknown>;
  channel_token?: string;
  agents?: ChannelAgent[];
  created_at: string;
  updated_at: string;
}

export interface ChannelAgent {
  id: string;
  channel_id: string;
  agent_id: string;
  priority: number;
  created_at: string;
}

export interface MetaPage {
  id: string;
  name: string;
  instagram_account?: {
    id: string;
    username?: string;
  };
}

class ChannelsService {
  async getChannels(): Promise<Channel[]> {
    const response = await apiClient.get<ApiResponse<Channel[]>>('/channels');
    return response.data.data || [];
  }

  async connectTelegram(botToken: string, agentIds: string[], name?: string): Promise<Channel> {
    const response = await apiClient.post<ApiResponse<Channel>>('/channels/telegram/connect', {
      bot_token: botToken,
      agent_ids: agentIds,
      name,
    });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error((response.data as any).error?.message || 'Failed to connect Telegram bot');
  }

  async createCustomChannel(name: string, webhookUrl?: string, agentIds?: string[]): Promise<Channel> {
    const response = await apiClient.post<ApiResponse<Channel>>('/channels/custom/create', {
      name,
      webhook_url: webhookUrl || undefined,
      agent_ids: agentIds,
    });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error((response.data as any).error?.message || 'Failed to create custom channel');
  }

  // ============= Meta (Facebook / Instagram) =============

  async getMetaOAuthUrl(redirectUri: string): Promise<{ url: string }> {
    const response = await apiClient.post<ApiResponse<{ url: string }>>('/channels/meta/oauth-url', {
      redirect_uri: redirectUri,
    });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error((response.data as any).error?.message || 'Failed to get Meta OAuth URL');
  }

  async handleMetaCallback(code: string, redirectUri: string): Promise<{ pages: MetaPage[] }> {
    const response = await apiClient.post<ApiResponse<{ pages: MetaPage[] }>>('/channels/meta/callback', {
      code,
      redirect_uri: redirectUri,
    });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error((response.data as any).error?.message || 'Failed to handle Meta callback');
  }

  async connectMeta(data: {
    page_id: string;
    page_name: string;
    channel_type: 'facebook' | 'instagram';
    agent_ids: string[];
    instagram_account_id?: string;
  }): Promise<Channel> {
    const response = await apiClient.post<ApiResponse<Channel>>('/channels/meta/connect', data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error((response.data as any).error?.message || 'Failed to connect Meta channel');
  }

  // ============= WhatsApp (Embedded Signup) =============

  async getWhatsAppOAuthUrl(redirectUri: string): Promise<{ url: string }> {
    const response = await apiClient.post<ApiResponse<{ url: string }>>('/channels/whatsapp/oauth-url', { redirect_uri: redirectUri });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error((response.data as any).error?.message || 'Failed to get WhatsApp OAuth URL');
  }

  async discoverWhatsApp(code: string, redirectUri: string): Promise<{ waba_id: string; waba_name: string; phone_numbers: { id: string; display_phone_number: string; verified_name: string }[]; message?: string }> {
    const response = await apiClient.post<ApiResponse<{ waba_id: string; waba_name: string; phone_numbers: { id: string; display_phone_number: string; verified_name: string }[]; message?: string }>>('/channels/whatsapp/discover', { code, redirect_uri: redirectUri });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error((response.data as any).error?.message || 'Failed to discover WhatsApp accounts');
  }

  async connectWhatsAppPhone(phoneNumberId: string, phoneNumber: string, displayName: string, agentIds: string[]): Promise<Channel> {
    const response = await apiClient.post<ApiResponse<Channel>>('/channels/whatsapp/connect', { phone_number_id: phoneNumberId, phone_number: phoneNumber, display_name: displayName, agent_ids: agentIds });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error((response.data as any).error?.message || 'Failed to connect WhatsApp');
  }

  // Legacy method kept for backward compat
  async handleWhatsAppCallback(code: string, redirectUri: string, agentIds: string[]): Promise<Channel> {
    const response = await apiClient.post<ApiResponse<Channel>>('/channels/whatsapp/callback', { code, redirect_uri: redirectUri, agent_ids: agentIds });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error((response.data as any).error?.message || 'Failed to connect WhatsApp');
  }

  // ============= Existing methods =============

  async regenerateToken(channelId: string): Promise<Channel> {
    const response = await apiClient.post<ApiResponse<Channel>>(`/channels/${channelId}/regenerate-token`);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to regenerate token');
  }

  async getChannelAgents(channelId: string): Promise<ChannelAgent[]> {
    const response = await apiClient.get<ApiResponse<ChannelAgent[]>>(`/channels/${channelId}/agents`);
    return response.data.data || [];
  }

  async addChannelAgent(channelId: string, agentId: string, priority?: number): Promise<ChannelAgent> {
    const response = await apiClient.post<ApiResponse<ChannelAgent>>(`/channels/${channelId}/agents`, {
      agent_id: agentId,
      priority: priority || 0,
    });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to add agent to channel');
  }

  async setChannelAgents(channelId: string, agents: Array<{ agent_id: string; priority?: number }>): Promise<ChannelAgent[]> {
    const response = await apiClient.put<ApiResponse<ChannelAgent[]>>(`/channels/${channelId}/agents`, {
      agents,
    });
    return response.data.data || [];
  }

  async removeChannelAgent(channelId: string, agentId: string): Promise<void> {
    await apiClient.delete(`/channels/${channelId}/agents/${agentId}`);
  }

  async updateChannel(id: string, data: {
    name?: string;
    webhook_url?: string;
    response_mode?: 'sync' | 'async';
    settings?: Record<string, unknown>;
    status?: string;
  }): Promise<Channel> {
    const response = await apiClient.put<ApiResponse<Channel>>(`/channels/${id}`, data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to update channel');
  }

  async disconnectChannel(id: string): Promise<void> {
    await apiClient.delete(`/channels/${id}`);
  }
}

export const channelsService = new ChannelsService();
